/**
 * User Hooks
 *
 * React Query hooks for user profile operations.
 */

import { useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  isTimeZone,
  type CitizenSetupInput,
  type IdentityDocumentReadInput,
  type ProfileFieldsInput,
} from '@tonnie/schemas';
import type { IdentityDocumentApplyField } from '@tonnie/types';
import { useSdkContext } from '../providers/SdkProvider';
import { useAuth } from '../providers/AuthProvider';
import {
  createUserService,
  type UserProfile,
  type UpdateProfileInput,
  type UpdateSettingsInput,
  type UserSettings,
  type CompleteOnboardingInput,
} from '../services/user.service';
import { journeyKeys } from './useJourney';
import { talentInvitationKeys } from './useTalentInvitations';
import { homeKeys } from './home-keys';
import { checkUpKeys } from './useCheckUp';
import { vaultKeys } from './useVault';
import { applySettingsPatch } from './settings-patch';

import { userKeys } from './user-keys';

export { userKeys };

/**
 * How complete is the citizen's profile? (W8.1 · F33)
 *
 * `staleTime` is short because the citizen fixes their profile in another tab
 * or on their phone and then comes back to press the button; the persistent
 * "your CheckUp is ready" banner the design specifies depends on this query
 * noticing.
 */
export function useProfileCompleteness() {
  const { client } = useSdkContext();
  const { isSignedIn, token } = useAuth();
  const userService = createUserService(client);

  return useQuery({
    queryKey: userKeys.profileCompleteness(),
    queryFn: () => userService.getProfileCompleteness(),
    enabled: isSignedIn && !!token,
    staleTime: 30 * 1000,
  });
}

/**
 * Get the global onboarding-status snapshot — `setupComplete` is what the
 * first run gates on (see `UserService.getOnboardingStatus`). Used by:
 *   - the setup gate and first-run router: send a citizen who is not set up
 *     to setup, everyone else on.
 *   - AcceptInvitePage: setup first only if not yet set up; otherwise straight
 *     to the freshly-activated journey.
 *
 * Backed by GET /api/users/me/onboarding-status. It changes only through the
 * writes that invalidate it — setup, onboarding completion, and a profile save
 * (which can supply the origin) — so a long staleness is safe.
 */
export function useOnboardingStatus() {
  const { client } = useSdkContext();
  const { isSignedIn, token } = useAuth();
  const userService = createUserService(client);

  return useQuery({
    queryKey: userKeys.onboardingStatus(),
    queryFn: () => userService.getOnboardingStatus(),
    enabled: isSignedIn && !!token,
    // Every write that can change it invalidates it (see above).
    staleTime: 60 * 60 * 1000,
  });
}

/**
 * Get current user profile
 */
export function useUserProfile() {
  const { client } = useSdkContext();
  const { isSignedIn, token } = useAuth();
  const userService = createUserService(client);

  return useQuery({
    queryKey: userKeys.profile(),
    queryFn: () => userService.getProfile(),
    enabled: isSignedIn && !!token,
    staleTime: 30 * 60 * 1000,
    refetchInterval: 45 * 60 * 1000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
}

/**
 * Keep the signed-in person's `users.timezone` in step with their device.
 *
 * Quiet hours are evaluated server-side in `users.timezone`, and a citizen
 * never sets it by hand — without this every citizen's window ran in UTC (a
 * "22:00–08:00" in São Paulo was really 19:00–05:00). The device knows where
 * the person is, and follows them when they travel.
 *
 * Writes only when the stored zone differs, once per value per session, and
 * never an unknown zone. For the citizen apps; business users pick theirs in
 * Settings.
 */
export function useDeviceTimezoneSync(deviceTimeZone: string | null | undefined): void {
  const { data: profile } = useUserProfile();
  const { mutate } = useUpdateProfile();
  const sent = useRef<string | null>(null);
  const loaded = profile !== undefined;
  const stored = profile?.timezone ?? null;

  useEffect(() => {
    if (!loaded || !deviceTimeZone || !isTimeZone(deviceTimeZone)) return;
    if (stored === deviceTimeZone || sent.current === deviceTimeZone) return;
    sent.current = deviceTimeZone;
    mutate({ timezone: deviceTimeZone });
  }, [loaded, stored, deviceTimeZone, mutate]);
}

/**
 * Update user profile
 *
 * Merges the PATCH response into the cached profile rather than replacing it.
 * Why: `PATCH /users/me` returns the raw DB row — `avatarUrl` is a storage
 * key, not a presigned URL. `GET /users/me` runs it through `resolveAvatarUrl`
 * before returning. A naive replace would overwrite the resolved URL with
 * the storage key, breaking the `<img>` tag until the next refetch. Merging
 * preserves the resolved URL, and invalidateQueries kicks off a fresh fetch
 * so any other fields stay in sync.
 */
export function useUpdateProfile() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const userService = createUserService(client);

  return useMutation({
    mutationFn: (input: UpdateProfileInput) => userService.updateProfile(input),
    onSuccess: (updatedProfile) => {
      queryClient.setQueryData<UserProfile | undefined>(userKeys.profile(), (old) => {
        if (!old) return updatedProfile;
        const oldAvatar = (old as unknown as { avatarUrl?: string | null }).avatarUrl;
        const incomingAvatar = (updatedProfile as unknown as { avatarUrl?: string | null })
          .avatarUrl;
        return {
          ...old,
          ...updatedProfile,
          // Keep the resolved presigned URL when the mutation response only
          // carries the raw S3 storage key.
          avatarUrl:
            incomingAvatar &&
            typeof incomingAvatar === 'string' &&
            incomingAvatar.startsWith('http')
              ? incomingAvatar
              : (oldAvatar ?? incomingAvatar),
        } as UserProfile;
      });
      queryClient.invalidateQueries({ queryKey: userKeys.profile() });
    },
  });
}

/**
 * Get user settings
 */
export function useUserSettings() {
  const { client } = useSdkContext();
  const { isSignedIn, token } = useAuth();
  const userService = createUserService(client);

  return useQuery({
    queryKey: userKeys.settings(),
    queryFn: () => userService.getSettings(),
    enabled: isSignedIn && !!token,
  });
}

/**
 * Update user settings — shown at once, and put back if the save fails.
 *
 * The switch moves the moment it is touched (the cached settings take the
 * patch — `applySettingsPatch`); if the request fails the cache returns to
 * what it was, so no screen goes on showing a choice the server does not
 * hold. On success the server's answer replaces the guess.
 */
export function useUpdateSettings() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const userService = createUserService(client);

  return useMutation<UserSettings, Error, UpdateSettingsInput, { previous?: UserSettings }>({
    mutationFn: (input) => userService.updateSettings(input),
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: userKeys.settings() });
      const previous = queryClient.getQueryData<UserSettings>(userKeys.settings());
      if (previous) {
        queryClient.setQueryData(userKeys.settings(), applySettingsPatch(previous, input));
      }
      return { previous };
    },
    onError: (_error, _input, context) => {
      if (context?.previous) queryClient.setQueryData(userKeys.settings(), context.previous);
    },
    onSuccess: (updatedSettings) => {
      queryClient.setQueryData(userKeys.settings(), updatedSettings);
    },
  });
}

/**
 * Upload user avatar to S3 and persist the storage key.
 */
export function useUploadAvatar() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const userService = createUserService(client);

  return useMutation({
    mutationFn: (file: File | { uri: string; name: string; type: string }) =>
      userService.uploadAvatar(file),
    onSuccess: ({ avatarUrl }) => {
      queryClient.setQueryData<UserProfile | undefined>(userKeys.profile(), (old) => {
        if (!old) return old;
        const existingCustomFields = (old.talent?.customFields ?? {}) as Record<string, unknown>;
        return {
          ...old,
          avatarUrl,
          talent: old.talent
            ? {
                ...old.talent,
                customFields: { ...existingCustomFields, avatarUrl },
              }
            : old.talent,
        };
      });
      queryClient.invalidateQueries({ queryKey: userKeys.profile() });
    },
  });
}

/**
 * Remove the user's avatar.
 *
 * Optimistically clears the cached avatar fields so the UI (header menu +
 * profile section) updates immediately, then invalidates the profile query
 * so the next fetch reflects the server state. Also scrubs
 * `talent.customFields.avatarUrl` from the cached profile to prevent the
 * onboarding wizard from re-promoting a stale key.
 */
export function useDeleteAvatar() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const userService = createUserService(client);

  return useMutation({
    mutationFn: () => userService.deleteAvatar(),
    onSuccess: () => {
      queryClient.setQueryData<UserProfile | undefined>(userKeys.profile(), (old) => {
        if (!old) return old;
        const existingCustomFields = (old.talent?.customFields ?? {}) as Record<string, unknown>;

        const { avatarUrl: _removed, ...customFieldsWithoutAvatar } = existingCustomFields;
        return {
          ...old,
          avatarUrl: null,
          imageUrl: undefined,
          talent: old.talent
            ? {
                ...old.talent,
                customFields: customFieldsWithoutAvatar,
              }
            : old.talent,
        };
      });
      queryClient.invalidateQueries({ queryKey: userKeys.profile() });
    },
  });
}

/**
 * Upload profile image (legacy alias).
 */
export function useUploadProfileImage() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const userService = createUserService(client);

  return useMutation({
    mutationFn: (file: File) => userService.uploadProfileImage(file),
    onSuccess: ({ imageUrl }) => {
      queryClient.setQueryData<UserProfile | undefined>(userKeys.profile(), (old) => {
        if (!old) return old;
        const existingCustomFields = (old.talent?.customFields ?? {}) as Record<string, unknown>;
        return {
          ...old,
          imageUrl,
          avatarUrl: imageUrl,
          talent: old.talent
            ? {
                ...old.talent,
                customFields: { ...existingCustomFields, avatarUrl: imageUrl },
              }
            : old.talent,
        };
      });
      queryClient.invalidateQueries({ queryKey: userKeys.profile() });
    },
  });
}

/**
 * Read an uploaded passport / ID card (its Vault file ids). Resolves to the
 * review; the profile is not touched until `useConfirmIdentityDocument`.
 */
export function useReadIdentityDocument() {
  const { client } = useSdkContext();
  const userService = createUserService(client);
  return useMutation({
    mutationFn: (input: IdentityDocumentReadInput) => userService.readIdentityDocument(input),
  });
}

/**
 * Write the ticked review lines to the profile — everything a profile save
 * refreshes refreshes here too, plus the Vault the document now sits in.
 */
export function useConfirmIdentityDocument() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const userService = createUserService(client);
  return useMutation({
    mutationFn: ({ id, apply }: { id: string; apply: IdentityDocumentApplyField[] }) =>
      userService.confirmIdentityDocument(id, apply),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.profile() });
      queryClient.invalidateQueries({ queryKey: userKeys.profileCompleteness() });
      queryClient.invalidateQueries({ queryKey: checkUpKeys.all });
      queryClient.invalidateQueries({ queryKey: userKeys.onboardingStatus() });
      queryClient.invalidateQueries({ queryKey: vaultKeys.all });
      // Home's five steps count these fields (essential / more data).
      queryClient.invalidateQueries({ queryKey: homeKeys.all });
    },
  });
}

/** Discard a document not yet confirmed — its Vault files go with it. */
export function useDiscardIdentityDocument() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const userService = createUserService(client);
  return useMutation({
    mutationFn: (id: string) => userService.discardIdentityDocument(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: vaultKeys.all }),
  });
}

/**
 * Save onboarding progress (without completing).
 * Called on each step transition to persist partial data.
 */
export function useSaveOnboardingProgress() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const userService = createUserService(client);

  return useMutation({
    mutationFn: (input: CompleteOnboardingInput) => userService.saveOnboardingProgress(input),
    onSuccess: () => {
      // Invalidate profile to re-fetch with the updated talent data
      queryClient.invalidateQueries({ queryKey: userKeys.profile() });
      // A progress save can fill one of the six fields the CheckUp score
      // needs (a profile section edits date of birth, phone, city, …), so
      // what those decide — completeness and the CheckUp readiness — refetch
      // too, exactly as after the focused profile form.
      queryClient.invalidateQueries({ queryKey: userKeys.profileCompleteness() });
      queryClient.invalidateQueries({ queryKey: checkUpKeys.all });
      // The country it can save is the origin `setupComplete` waits on.
      queryClient.invalidateQueries({ queryKey: userKeys.onboardingStatus() });
      // Invalidate onboarding progress so the next wizard mount sees
      // fresh data instead of a stale cache from the previous session.
      // Without this, React Query serves the cached payload on mount
      // before the refetch lands, and the hydration effect — which
      // runs once and locks in — can lock in stale values.
      queryClient.invalidateQueries({ queryKey: ['onboarding', 'progress'] });
      // CROSS-INVALIDATE: B2B onboarding writes land in tenant_settings,
      // which the dashboard reads via a DIFFERENT query key. Without
      // this the dashboard keeps showing pre-save values and the user
      // thinks the save didn't happen.
      queryClient.invalidateQueries({ queryKey: ['business', 'tenant-settings'] });
      queryClient.invalidateQueries({ queryKey: ['business', 'dashboard'] });
      // Home's five steps count the profile (essential / more data).
      queryClient.invalidateQueries({ queryKey: homeKeys.all });
    },
  });
}

/**
 * Save some of the citizen's profile fields — only those given, nothing else.
 * (F33 · Citizen first run · the focused "complete your profile" form)
 *
 * Goes through the non-destructive progress write: a field not sent is left
 * exactly as it is. On success it refreshes what those fields decide: the
 * profile, its completeness (the six the score needs), and the CheckUp
 * readiness — `awaitingProfile` clears the moment the last one is saved, so the
 * CheckUp page the citizen returns to offers "Calculate my score" in context.
 */
export function useSaveProfileFields() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const userService = createUserService(client);

  return useMutation({
    mutationFn: (input: ProfileFieldsInput) =>
      userService.saveOnboardingProgress({
        ...(input.firstName !== undefined && { firstName: input.firstName }),
        ...(input.lastName !== undefined && { lastName: input.lastName }),
        ...(input.phone !== undefined && { phone: input.phone }),
        ...(input.phoneCountryCode !== undefined && { phoneCountryCode: input.phoneCountryCode }),
        ...(input.dateOfBirth !== undefined && { dateOfBirth: input.dateOfBirth }),
        // The whole list, in the citizen's order — the first is the one the
        // profile shows first (D-22).
        ...(input.citizenships !== undefined && { nationalities: input.citizenships }),
        ...(input.currentCountry !== undefined && { currentCountry: input.currentCountry }),
        ...(input.currentCity !== undefined && { currentCity: input.currentCity }),
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: userKeys.profile() }),
        queryClient.invalidateQueries({ queryKey: userKeys.profileCompleteness() }),
        // The country saved here is the origin `setupComplete` waits on.
        queryClient.invalidateQueries({ queryKey: userKeys.onboardingStatus() }),
        queryClient.invalidateQueries({ queryKey: checkUpKeys.all }),
        queryClient.invalidateQueries({ queryKey: ['onboarding', 'progress'] }),
        // Home's "Add essential data" step counts these same fields.
        queryClient.invalidateQueries({ queryKey: homeKeys.all }),
      ]);
    },
  });
}

/**
 * Complete onboarding with full payload
 */
export function useCompleteOnboarding() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const userService = createUserService(client);

  return useMutation({
    mutationFn: (input?: CompleteOnboardingInput) => userService.completeOnboarding(input),
    onSuccess: () => {
      // Update onboarding status in profile cache
      queryClient.setQueryData<UserProfile | undefined>(userKeys.profile(), (old) =>
        old ? { ...old, onboardingCompleted: true } : undefined
      );
      // Invalidate profile to re-fetch with the new talent data
      queryClient.invalidateQueries({ queryKey: userKeys.profile() });
      // Completion stamps the citizen as set up — the gate must see it.
      queryClient.invalidateQueries({ queryKey: userKeys.onboardingStatus() });
      // Invalidate onboarding progress so the next wizard mount fetches
      // the real post-complete state. See useSaveOnboardingProgress for
      // context on the stale-cache bug this avoids.
      queryClient.invalidateQueries({ queryKey: ['onboarding', 'progress'] });
      // CROSS-INVALIDATE: tenant_settings is the underlying source of
      // truth for B2B onboarding. The dashboard reads it through a
      // different query key, so we must invalidate both to keep them
      // in sync after completion.
      queryClient.invalidateQueries({ queryKey: ['business', 'tenant-settings'] });
      queryClient.invalidateQueries({ queryKey: ['business', 'dashboard'] });
      // CROSS-INVALIDATE: journey list + current journey caches.
      // The platform-api `completeOnboarding` handler backfills
      // `origin_country` from the citizen's address and transitions
      // every `pending_setup` invitation journey → `active`. Without
      // invalidating here the dashboard renders the pre-onboarding
      // snapshot (NULL origin, "Setup pending" banner) even though
      // the DB row is already correct (BR → PT, active, invited_only).
      // See `users.service.ts:completeOnboarding` for the backend
      // side of this contract.
      queryClient.invalidateQueries({ queryKey: journeyKeys.lists() });
      queryClient.invalidateQueries({ queryKey: journeyKeys.current() });
    },
  });
}

/**
 * The citizen's one-screen setup. (Citizen first run)
 *
 * On success everything the answer changes is refetched: the onboarding
 * status the first-run router reads, the profile and its completeness (the
 * country and city are two of the six score fields), the journey caches (a
 * company journey waiting on an origin has just become `active`), and the
 * invitation list (setup runs the invite-match for citizens who signed up
 * without their link).
 */
export function useCompleteCitizenSetup() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const userService = createUserService(client);

  return useMutation({
    mutationFn: (input: CitizenSetupInput) => userService.completeSetup(input),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: userKeys.onboardingStatus() }),
        queryClient.invalidateQueries({ queryKey: userKeys.profile() }),
        queryClient.invalidateQueries({ queryKey: userKeys.profileCompleteness() }),
        queryClient.invalidateQueries({ queryKey: journeyKeys.lists() }),
        queryClient.invalidateQueries({ queryKey: journeyKeys.current() }),
        queryClient.invalidateQueries({ queryKey: talentInvitationKeys.mine() }),
        // A journey that just opened moves Home to another moment.
        queryClient.invalidateQueries({ queryKey: homeKeys.all }),
      ]);
    },
  });
}

/**
 * Delete user account
 */
export function useDeleteAccount() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const userService = createUserService(client);

  return useMutation({
    mutationFn: () => userService.deleteAccount(),
    onSuccess: () => {
      // Clear all cached data
      queryClient.clear();
    },
  });
}
